import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { NAV, PILLARS, CONTACT, LOGO_WHITE } from "@/data/content";
import { useLang } from "@/context/LanguageContext";

export const Footer = () => {
  const { t } = useLang();
  const [email, setEmail] = useState("");

  const subscribe = (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    toast.info("Newsletter subscriptions are not available yet. Please use our contact page to get in touch.");
    setEmail("");
  };

  return (
    <footer className="relative border-t border-white/10 bg-[#262A30]" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <img src={LOGO_WHITE} alt="ERAM DIGITAL" width="421" height="183" className="h-14 w-auto" data-testid="footer-logo" />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-400">{t("footer.tagline")}</p>
            <div className="mt-6 space-y-2 text-sm text-slate-400">
              <p className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 text-brand-violet" />{CONTACT.hq}</p>
              <p className="flex items-center gap-2"><Mail className="h-4 w-4 shrink-0 text-[#F08089]" /><a href={`mailto:${CONTACT.email}`} className="hover:text-white" data-testid="footer-email-link">{CONTACT.email}</a></p>
              <p className="flex items-center gap-2"><Phone className="h-4 w-4 shrink-0 text-[#F08089]" /><a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="hover:text-white" data-testid="footer-phone-link">{CONTACT.phone}</a></p>
                <p className="flex items-center gap-2"><Phone className="h-4 w-4 shrink-0 text-[#F08089]" /><a href={`tel:${CONTACT.secondaryPhone.replace(/\s/g, "")}`} className="hover:text-white" data-testid="footer-secondary-phone-link">{CONTACT.secondaryPhone}</a></p>
                <p className="pt-2 leading-relaxed" data-testid="footer-regional-offices">Regional offices<br />{CONTACT.uae}<br />{CONTACT.bahrain}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:col-span-5">
            <div>
              <h4 className="mb-4 text-xs font-semibold uppercase tracking-widest text-slate-500">{t("footer.company")}</h4>
              <ul className="space-y-2.5 text-sm">
                {NAV.filter((n) => ["home", "about", "careers", "contact"].includes(n.key)).map((n) => (
                  <li key={n.key}><Link to={n.path} data-testid={`footer-nav-${n.key}`} className="text-slate-400 transition-colors hover:text-white">{t(`nav.${n.key}`)}</Link></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="mb-4 text-xs font-semibold uppercase tracking-widest text-slate-500">{t("footer.solutions")}</h4>
              <ul className="space-y-2.5 text-sm">
                {PILLARS.map((p) => (
                  <li key={p.id}><Link to={`/services#${p.id}`} data-testid={`footer-service-${p.id}`} className="text-slate-400 transition-colors hover:text-white">{p.name}</Link></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="mb-4 text-xs font-semibold uppercase tracking-widest text-slate-500">{t("footer.resources")}</h4>
              <ul className="space-y-2.5 text-sm">
                <li><Link to="/case-studies" className="text-slate-400 transition-colors hover:text-white">{t("nav.cases")}</Link></li>
                <li><Link to="/industries" className="text-slate-400 transition-colors hover:text-white">{t("nav.industries")}</Link></li>
                <li><Link to="/services" className="text-slate-400 transition-colors hover:text-white">{t("nav.services")}</Link></li>
              </ul>
            </div>
          </div>

          <div className="lg:col-span-3">
            <h4 className="mb-4 text-xs font-semibold uppercase tracking-widest text-slate-500">{t("footer.newsletter")}</h4>
            <p className="mb-4 text-sm text-slate-400">{t("footer.newsletterDesc")}</p>
            <form onSubmit={subscribe} className="flex gap-2" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="min-w-0 flex-1 rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-brand-violet focus:outline-none"
                data-testid="newsletter-email"
              />
              <button type="submit" className="flex items-center justify-center rounded-full gradient-bg px-4 py-2.5 text-white transition-transform hover:scale-105" data-testid="newsletter-submit" aria-label={t("footer.subscribe")}>
                <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-xs text-slate-500 sm:flex-row">
          <p>© {new Date().getFullYear()} ERAM DIGITAL, part of ERAM HOLDINGS. {t("footer.rights")}</p>
          <Link to="/contact" className="transition-colors hover:text-white" data-testid="footer-legal-inquiries">Privacy & legal inquiries</Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
